import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { localDate, localMonth } from '../utils/localDate';

export const EMOTIONS = [
  { id: 'happy', label: 'Joyeuse', img: '/emotions/happy.png' },
  { id: 'serein', label: 'Sereine', img: '/emotions/serein.png' },
  { id: 'reconnaissant', label: 'Reconnaissante', img: '/emotions/reconnaissant.png' },
  { id: 'anxieux', label: 'Anxieuse', img: '/emotions/anxieux.png' },
  { id: 'stressed', label: 'Stressée', img: '/emotions/stressed.png' },
  { id: 'fatigue', label: 'Fatiguée', img: '/emotions/fatigue.png' },
];

export const MoodPicker = ({ autoPrompt = false }) => {
  const { isAuthenticated, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [today, setToday] = useState(null);
  const [recap, setRecap] = useState(null);
  const [showRecap, setShowRecap] = useState(false);
  const autoOpened = useRef(false);

  const date = localDate();
  const seenKey = user ? `mood_prompt_seen_${user.id}_${date}` : null;

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    api
      .getMoodStatus(date)
      .then((d) => {
        if (d.emotion) setToday(d.emotion);
        if (d.prompt_seen && seenKey) localStorage.setItem(seenKey, '1');
        const alreadySeen = d.prompt_seen || (seenKey && localStorage.getItem(seenKey));
        if (autoPrompt && !alreadySeen && !autoOpened.current) {
          autoOpened.current = true;
          setTimeout(() => setOpen(true), 500);
        }
      })
      .catch(() => {});
  }, [isAuthenticated, user]);

  const markSeen = () => {
    if (seenKey) localStorage.setItem(seenKey, '1');
    api.markMoodPromptSeen(date).catch(() => {});
  };

  const close = () => {
    setOpen(false);
    markSeen();
  };

  const select = async (id) => {
    setToday(id);
    try {
      await api.saveMood(id, date);
    } catch {}
    if (seenKey) localStorage.setItem(seenKey, '1');
    setTimeout(() => setOpen(false), 450);
  };

  const loadRecap = async () => {
    try {
      const r = await api.getMoodRecap(localMonth());
      setRecap(r);
      setShowRecap(true);
    } catch {}
  };

  if (!isAuthenticated) return null;

  const todayEmotion = EMOTIONS.find((e) => e.id === today);

  return (
    <>
      <button
        onClick={() => { setOpen(true); setShowRecap(false); }}
        data-testid="mood-picker-button"
        aria-label="Mon humeur du jour"
        className="ml-auto flex items-center justify-center w-10 h-10 rounded-full border border-gray-300 bg-[#FAF9F6] shadow-sm active:scale-95 transition-transform"
      >
        <img src={todayEmotion ? todayEmotion.img : '/emotions/happy.png'} alt="Mon humeur" className="w-7 h-7 object-contain" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-5" data-testid="mood-modal">
          <div className="absolute inset-0 bg-black/30" onClick={close} />
          <div className="relative bg-[#FAF9F6] rounded-3xl p-6 w-full max-w-sm shadow-xl border border-gray-200">
            <button
              onClick={close}
              data-testid="mood-modal-close"
              className="absolute top-3 right-3 p-1.5 rounded-full text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>

            {!showRecap ? (
              <>
                <h2 className="text-center text-lg text-gray-800 mb-5" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>
                  Comment te sens-tu aujourd'hui&nbsp;?
                </h2>
                <div className="grid grid-cols-3 gap-3">
                  {EMOTIONS.map((e) => (
                    <button
                      key={e.id}
                      onClick={() => select(e.id)}
                      data-testid={`mood-${e.id}`}
                      className={`flex flex-col items-center p-2 rounded-2xl border transition-all active:scale-95 ${
                        today === e.id ? 'border-[#F79AAE] bg-[#F79AAE]/10' : 'border-gray-200'
                      }`}
                    >
                      <img src={e.img} alt={e.label} className="w-16 h-16 object-contain" />
                      <span className="text-xs text-gray-600 mt-1">{e.label}</span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-5">
                  <button
                    onClick={close}
                    data-testid="mood-later-button"
                    className="text-sm text-gray-400 underline underline-offset-2"
                  >
                    Plus tard
                  </button>
                  <button
                    onClick={loadRecap}
                    data-testid="mood-recap-button"
                    className="text-sm text-[#F79AAE] underline underline-offset-2"
                  >
                    Mon récap du mois
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-center text-lg text-gray-800 mb-5" style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600 }}>
                  Mes émotions du mois
                </h2>
                {recap && recap.total >= 3 ? (() => {
                  const dominant = EMOTIONS.reduce((best, e) =>
                    (recap.counts[e.id] || 0) > (recap.counts[best.id] || 0) ? e : best
                  , EMOTIONS[0]);
                  const SOOTHING = {
                    happy: 'Que cette joie continue de rayonner en toi.',
                    serein: 'Garde cette sérénité précieusement.',
                    reconnaissant: 'Un cœur reconnaissant attire la lumière.',
                    anxieux: 'Sois douce avec toi-même, chaque souffle apaise.',
                    stressed: 'Accorde-toi des instants de calme, tu les mérites.',
                    fatigue: 'Ton corps te parle, offre-lui du repos.',
                  };
                  const others = EMOTIONS.filter((e) => recap.counts[e.id] && e.id !== dominant.id);
                  return (
                    <div className="text-center" data-testid="mood-recap-content">
                      <img src={dominant.img} alt={dominant.label} className="w-24 h-24 object-contain mx-auto" data-testid="mood-recap-dominant" />
                      <p className="text-sm text-gray-700 mt-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        Ce mois-ci, <span className="text-[#F79AAE]">{dominant.label.toLowerCase()}</span> a fleuri en toi.
                      </p>
                      <p className="text-[11px] text-gray-400 font-light mt-1" data-testid="mood-recap-count">
                        {recap.total} ressentis enregistrés ce mois-ci
                      </p>
                      <p className="text-[13px] text-gray-400 font-light italic mt-3 px-4 leading-relaxed" data-testid="mood-recap-word">
                        {SOOTHING[dominant.id]}
                      </p>
                      {others.length > 0 && (
                        <div className="flex justify-center gap-4 mt-6" data-testid="mood-recap-others">
                          {others.map((e) => (
                            <div key={e.id} className="flex flex-col items-center">
                              <img src={e.img} alt={e.label} className="w-9 h-9 object-contain" />
                              <span className="text-[10px] text-gray-400 mt-0.5">{recap.counts[e.id]}j</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })() : (
                  <p className="text-sm text-gray-500 text-center" data-testid="mood-recap-empty">
                    {recap && recap.total > 0
                      ? `${recap.total} ressenti${recap.total > 1 ? 's' : ''} enregistré${recap.total > 1 ? 's' : ''} ce mois-ci.`
                      : 'Aucune émotion enregistrée ce mois-ci.'}
                  </p>
                )}
                <button onClick={() => setShowRecap(false)} className="mt-5 w-full text-sm text-[#F79AAE] underline underline-offset-2">
                  ← Retour
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
